import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { REASONS, DECIDING_FACTORS, DIFFICULTIES } from "../data/gameData";

const Pill = ({ selected, onClick, children, testId, disabled }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${
      selected
        ? "bg-[#FF107A] text-[#FDFBF7] border-[#FF107A] glow-pink"
        : disabled
        ? "bg-white/5 text-[#FDFBF7]/30 border-white/10 cursor-not-allowed"
        : "bg-white/5 text-[#FDFBF7] border-white/15 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10"
    }`}
  >
    {children}
  </button>
);

const MoreToggle = ({ expanded, hiddenCount, onToggle, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onToggle}
    className="rounded-full px-4 py-2 text-sm font-semibold border border-dashed border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/5 hover:bg-[#00E5FF]/15 transition-all"
  >
    {expanded ? "Show less" : `+ ${hiddenCount} more`}
  </button>
);

const INITIAL_VISIBLE = 3;

export const ReasonPicker = ({ selected, onChange, max = 2 }) => {
  // Auto-expand if any selected sits outside the initial 3.
  const forceExpand = useMemo(
    () => selected.some((id) => REASONS.findIndex((r) => r.id === id) >= INITIAL_VISIBLE),
    [selected]
  );
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || forceExpand;

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((r) => r !== id));
    else if (selected.length < max) onChange([...selected, id]);
  };

  const visible = isExpanded ? REASONS : REASONS.slice(0, INITIAL_VISIBLE);
  const hiddenCount = REASONS.length - INITIAL_VISIBLE;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold">
          Why did you choose them?
        </p>
        <span className="text-xs text-[#FDFBF7]/50">Pick up to {max}</span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="reason-picker">
        <AnimatePresence initial={false}>
          {visible.map((r) => {
            const isSel = selected.includes(r.id);
            const disabled = !isSel && selected.length >= max;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.18 }}
              >
                <Pill
                  selected={isSel}
                  onClick={() => toggle(r.id)}
                  disabled={disabled}
                  testId={`reason-${r.id}`}
                >
                  {r.label}
                </Pill>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {hiddenCount > 0 && !forceExpand && (
          <MoreToggle
            expanded={expanded}
            hiddenCount={hiddenCount}
            onToggle={() => setExpanded((v) => !v)}
            testId="reason-more-btn"
          />
        )}
      </div>
    </div>
  );
};

export const DecidingFactorPicker = ({ selected, onChange }) => {
  const forceExpand = useMemo(
    () => selected && DECIDING_FACTORS.indexOf(selected) >= INITIAL_VISIBLE,
    [selected]
  );
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || forceExpand;
  const visible = isExpanded ? DECIDING_FACTORS : DECIDING_FACTORS.slice(0, INITIAL_VISIBLE);
  const hiddenCount = DECIDING_FACTORS.length - INITIAL_VISIBLE;

  return (
    <div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-3">
        What was the deciding factor?
      </p>
      <div className="flex flex-wrap gap-2" data-testid="deciding-factor-picker">
        <AnimatePresence initial={false}>
          {visible.map((f) => (
            <motion.div
              key={f}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.18 }}
            >
              <Pill
                selected={selected === f}
                onClick={() => onChange(f)}
                testId={`factor-${f.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {f}
              </Pill>
            </motion.div>
          ))}
        </AnimatePresence>
        {hiddenCount > 0 && !forceExpand && (
          <MoreToggle
            expanded={expanded}
            hiddenCount={hiddenCount}
            onToggle={() => setExpanded((v) => !v)}
            testId="factor-more-btn"
          />
        )}
      </div>
    </div>
  );
};

export const DifficultyPicker = ({ selected, onChange }) => (
  <div>
    <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] font-semibold mb-3">
      How difficult was the choice?
    </p>
    <div className="grid grid-cols-2 gap-2" data-testid="difficulty-picker">
      {DIFFICULTIES.map((d) => (
        <button
          key={d.id}
          data-testid={`difficulty-${d.id}`}
          onClick={() => onChange(d.id)}
          className={`rounded-xl border-2 py-3 px-3 text-left transition-all ${
            selected === d.id
              ? "border-[#FF107A] bg-[#FF107A]/10 text-[#FDFBF7]"
              : "border-white/15 bg-white/5 text-[#FDFBF7]/80 hover:border-[#A855F7]"
          }`}
        >
          <div className="font-display text-lg tracking-wide">{d.label}</div>
        </button>
      ))}
    </div>
  </div>
);

/**
 * Compact factor picker used for optional/secondary lists
 * (predicted majority reason, Custom Builder). Also collapses to 3 + more.
 */
export const CompactFactorPicker = ({
  selected,
  onChange,
  options = DECIDING_FACTORS,
  activeColor = "#00E5FF",
  testIdPrefix,
  allowDeselect = true,
}) => {
  const forceExpand = useMemo(
    () => selected && options.indexOf(selected) >= INITIAL_VISIBLE,
    [selected, options]
  );
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || forceExpand;
  const visible = isExpanded ? options : options.slice(0, INITIAL_VISIBLE);
  const hiddenCount = options.length - INITIAL_VISIBLE;

  return (
    <div className="flex flex-wrap gap-2" data-testid={testIdPrefix}>
      <AnimatePresence initial={false}>
        {visible.map((f) => {
          const isSel = selected === f;
          return (
            <motion.button
              key={f}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.18 }}
              type="button"
              data-testid={`${testIdPrefix}-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => onChange(isSel && allowDeselect ? null : f)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-all ${
                isSel
                  ? "border-transparent"
                  : "bg-white/5 text-[#FDFBF7]/80 border-white/15 hover:border-[#00E5FF]"
              }`}
              style={
                isSel
                  ? { background: activeColor, color: "#0A090D", borderColor: activeColor }
                  : undefined
              }
            >
              {f}
            </motion.button>
          );
        })}
      </AnimatePresence>
      {hiddenCount > 0 && !forceExpand && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-more-btn`}
          onClick={() => setExpanded((v) => !v)}
          className="rounded-full px-3 py-1.5 text-xs font-semibold border border-dashed border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/5 hover:bg-[#00E5FF]/15 transition-all"
        >
          {expanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};

/**
 * Compact reason picker (multi-select up to max) used in Custom Builder's ReasonsQuick.
 */
export const CompactReasonPicker = ({ selected, onChange, max = 2, testIdPrefix }) => {
  const forceExpand = useMemo(
    () => selected.some((id) => REASONS.findIndex((r) => r.id === id) >= INITIAL_VISIBLE),
    [selected]
  );
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || forceExpand;
  const visible = isExpanded ? REASONS : REASONS.slice(0, INITIAL_VISIBLE);
  const hiddenCount = REASONS.length - INITIAL_VISIBLE;

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else if (selected.length < max) onChange([...selected, id]);
  };

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testIdPrefix}>
      <AnimatePresence initial={false}>
        {visible.map((r) => {
          const isSel = selected.includes(r.id);
          const disabled = !isSel && selected.length >= max;
          return (
            <motion.button
              key={r.id}
              type="button"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.18 }}
              onClick={() => toggle(r.id)}
              disabled={disabled}
              data-testid={`${testIdPrefix}-${r.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                isSel
                  ? "bg-[#FF107A] border-[#FF107A] text-[#FDFBF7]"
                  : disabled
                  ? "bg-white/5 border-white/10 text-[#FDFBF7]/30 cursor-not-allowed"
                  : "bg-white/5 border-white/15 hover:border-[#00E5FF]"
              }`}
            >
              {r.label}
            </motion.button>
          );
        })}
      </AnimatePresence>
      {hiddenCount > 0 && !forceExpand && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-more-btn`}
          onClick={() => setExpanded((v) => !v)}
          className="text-xs px-3 py-1.5 rounded-full border border-dashed border-[#00E5FF]/50 text-[#00E5FF] bg-[#00E5FF]/5 hover:bg-[#00E5FF]/15"
        >
          {expanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};
